import { Box, Button, SizableText, SmartLink, XStack, YStack, colors } from '@my/ui'
import { GroupAnnouncement, User } from 'app/types'
import { MessageCircle } from '@tamagui/lucide-icons'
import { PostReactions, PostText, PostUser, GroupUser } from '..'
import { GroupPostOptions, GroupPostReactionPopupContainer } from '../../containers'
import { useLink } from 'solito/link'
import { format } from 'date-fns'

export interface GroupAnnouncementCardProps {
  user: Pick<User, 'id' | 'fullName' | 'avatar'> | null
  announcement: GroupAnnouncement
  displayAs?: 'feedItem' | 'post'
  canInteract: boolean
  canExpandContent?: boolean
  onAfterDelete: () => void
}

export const GroupAnnouncementCard = ({
  displayAs = 'post',
  user,
  announcement,
  canInteract,
  canExpandContent,
  onAfterDelete,
}: GroupAnnouncementCardProps) => {
  const activeReaction = announcement.reactions.find((reaction) => reaction.userId === user?.id)
  const totalReactions =
    announcement.reactions?.reduce((acc, reaction) => acc + reaction.count, 0) || 0

  const { onPress } = useLink({
    href: announcement.href,
  })

  const scheduledDate = announcement.scheduledDate && new Date(announcement.scheduledDate)

  return (
    <Box ai="stretch" fd="column" gap="$3.5">
      <XStack flex={1} jc="space-between" ai="flex-start">
        {displayAs === 'post' && (
          <PostUser
            creator={announcement.creator}
            group={announcement.group}
            createdAt={announcement.createdAt}
            editedAt={announcement.editedAt}
          />
        )}
        {displayAs === 'feedItem' && (
          <GroupUser
            creator={announcement.creator}
            group={announcement.group}
            createdAt={announcement.createdAt}
            editedAt={announcement.editedAt}
          />
        )}
        <XStack gap="$2">
          <GroupPostOptions post={announcement} onAfterDelete={onAfterDelete} />
        </XStack>
      </XStack>

      {/* Fake button so that we don't nest links */}
      <YStack
        role="button"
        cursor="pointer"
        gap="$3.5"
        onPress={onPress}
        hoverStyle={{
          opacity: 0.98,
        }}
        focusStyle={{
          opacity: 0.95,
        }}
        pressStyle={{
          opacity: 0.85,
        }}
      >
        <SizableText size="$6" fow="600" $gtSm={{ size: '$8.5', fow: '600' }}>
          {announcement.title}
        </SizableText>
        <PostText text={announcement.body} expandable={canExpandContent} />
      </YStack>

      <YStack gap="$2">
        <XStack flex={1} jc="space-between" ai="center">
          <PostReactions reactions={announcement.reactions || []} />
          <XStack ml="auto" gap="$2">
            {canInteract && (
              <GroupPostReactionPopupContainer
                postId={announcement.id}
                user={user}
                activeReaction={activeReaction}
                totalReactions={totalReactions}
              />
            )}
            <SmartLink
              href={announcement.href}
              Component={Button}
              plain
              size="$2"
              icon={<MessageCircle size={24} color={colors.gray11} />}
            >
              <SizableText color="$gray11" size="$1" fow="500">
                {announcement.totalComments}
              </SizableText>
            </SmartLink>
          </XStack>
        </XStack>
        {scheduledDate && scheduledDate > new Date() && user?.id === announcement.creator.id && (
          <SizableText color="$gray11" size="$1" fow="500">
            Due at {format(scheduledDate, 'MMM d, yyyy h:mm a')}
          </SizableText>
        )}
      </YStack>
    </Box>
  )
}
